import { Flex, Box, Menu, Portal } from '@chakra-ui/react';
import { LuPencil, LuGlobe, LuCheck, LuCamera } from 'react-icons/lu';
import type { MenuTheme } from '../../api/menus';
import { NATIVE_LANGUAGE_NAMES } from './types';
import InlineEdit from './InlineEdit';

interface HeaderBannerProps {
  name: string;
  headerImageUrl: string;
  theme: MenuTheme;
  isEditMode: boolean;
  availableLanguages: string[];
  currentLanguage: string;
  onLanguageChange?: (lang: string) => void;
  onHeaderImageClick?: () => void;
  onQuickEditHeaderImage?: () => void;
  onQuickEditTitle?: (name: string) => void;
}

const HeaderBanner = ({
  name,
  headerImageUrl,
  theme: _mt,
  isEditMode,
  availableLanguages,
  currentLanguage,
  onLanguageChange,
  onHeaderImageClick,
  onQuickEditHeaderImage,
  onQuickEditTitle,
}: HeaderBannerProps) => {
  const isQuickEditMode = !!onQuickEditHeaderImage;
  const isClickable = (isEditMode && onHeaderImageClick) || isQuickEditMode;
  const handleClick = isEditMode && onHeaderImageClick ? onHeaderImageClick : isQuickEditMode ? onQuickEditHeaderImage : undefined;

  return (
    <Box position="relative">
      {/* Image layer — in quick edit mode, this has its own hover overlay */}
      <Box
        position="relative"
        cursor={isClickable ? 'pointer' : undefined}
        onClick={isClickable ? handleClick : undefined}
        css={isClickable ? { '&:hover .edit-overlay': { opacity: 1 } } : undefined}
        borderWidth={isEditMode && onHeaderImageClick ? '2px' : undefined}
        borderStyle={isEditMode && onHeaderImageClick ? 'solid' : undefined}
        borderColor={isEditMode && onHeaderImageClick ? 'transparent' : undefined}
        transition={isClickable ? 'border-color 0.15s' : undefined}
        _hover={isEditMode && onHeaderImageClick ? { borderColor: 'rgba(59, 130, 246, 0.5)' } : undefined}
      >
        <img src={headerImageUrl} alt="" style={{ width: '100%', display: 'block', aspectRatio: '2 / 1', objectFit: 'cover', filter: 'brightness(0.55)' }} />
        {isClickable && (
          <Flex
            className="edit-overlay"
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            justifyContent="center"
            alignItems="center"
            backgroundColor="blackAlpha.400"
            opacity={0}
            transition="opacity 0.15s"
            pointerEvents="none"
          >
            <Flex backgroundColor="blackAlpha.600" borderRadius="full" p="8px" color="white">
              {isQuickEditMode ? <LuCamera size={16} /> : <LuPencil size={16} />}
            </Flex>
          </Flex>
        )}
      </Box>

      {/* Title overlay */}
      <Flex position="absolute" top="0" left="0" right="0" bottom="0" justifyContent="center" alignItems="center" px="20px" zIndex={2} pointerEvents="none">
        {isQuickEditMode && onQuickEditTitle ? (
          <Box textAlign="center" width="100%" cursor="text" pointerEvents="auto">
            <InlineEdit
              value={name}
              onSave={onQuickEditTitle}
              fontSize="28px"
              fontWeight="600"
              color="white"
              lineHeight="130%"
              textAlign="center"
            />
          </Box>
        ) : (
          <Box fontSize="28px" fontWeight="600" color="white" textAlign="center" lineHeight="130%" textShadow="0 1px 4px rgba(0,0,0,0.4)">
            {name}
          </Box>
        )}
      </Flex>

      {availableLanguages.length > 1 && onLanguageChange && (
        <Box position="absolute" top="10px" right="10px" zIndex={3} onClick={(e) => e.stopPropagation()}>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap="6px"
                py="6px"
                px="10px"
                fontSize="13px"
                fontWeight="400"
                color="white"
                backgroundColor="rgba(0,0,0,0.75)"
                borderRadius="6px"
                cursor="pointer"
                _hover={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
              >
                <LuGlobe style={{ width: 14, height: 14 }} />
                {NATIVE_LANGUAGE_NAMES[currentLanguage] || currentLanguage.toUpperCase()}
              </Box>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner style={{ marginTop: '-4px' }}>
                <Menu.Content
                  backgroundColor="#1A1A1A"
                  borderWidth="1px"
                  borderColor="#333333"
                  borderRadius="8px"
                  minWidth="160px"
                  p="4px"
                  css={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
                >
                  {availableLanguages.map((code) => {
                    const label = NATIVE_LANGUAGE_NAMES[code] || code.toUpperCase();
                    return (
                      <Menu.Item
                        key={code}
                        value={code}
                        onClick={() => onLanguageChange(code)}
                        fontSize="13px"
                        fontWeight="400"
                        color={currentLanguage === code ? '#FFFFFF' : '#999999'}
                        backgroundColor={currentLanguage === code ? '#333333' : 'transparent'}
                        borderRadius="6px"
                        px="10px"
                        py="7px"
                        _hover={{ backgroundColor: '#333333', color: '#FFFFFF' }}
                        cursor="pointer"
                      >
                        <Flex alignItems="center" justifyContent="space-between" width="100%">
                          {label}
                          {currentLanguage === code && <LuCheck style={{ width: 14, height: 14 }} />}
                        </Flex>
                      </Menu.Item>
                    );
                  })}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Box>
      )}
    </Box>
  );
};

export default HeaderBanner;
